/**
 * Strip complete and incomplete Pure LLM control tags from live streaming answer
 * text so the chat bubble never shows raw markup mid-stream.
 *
 * Tags: `<chart-op>`, `<mutation-mode>`, `<search-request>`, `<context-suggest>`.
 *
 * Complete tags: removed entirely (quoted attribute values may contain `>`).
 * Incomplete trailing fragments (`<chart-op…` without a closing `>`): truncated
 * from the first such open so partial tags stay hidden until the next delta
 * completes them (those completed tags are then stripped on a later pass).
 * Orphan residues after a broken/split strip (lone `/>` lines, attribute-soup
 * lines ending in `/>`) are also removed outside fenced code blocks.
 *
 * HTML comments (`<!-- ... -->` and the common malformed `<!-- ... ->`) are
 * stripped outside fences so LLM section markers never leak into the bubble.
 */

const CONTROL_TAG_NAMES = [
    'chart-op',
    'mutation-mode',
    'search-request',
    'context-suggest',
];

const TAG_NAME_ALT = CONTROL_TAG_NAMES.join('|');

/** Quoted attrs may contain `>`; unquoted spans may not. */
const COMPLETE_CONTROL_TAG_RE = new RegExp(
    `<(?:${TAG_NAME_ALT})\\b(?:[^>"']|"[^"]*"|'[^']*')*\\/?\\s*>`,
    'gi'
);

/**
 * Any remaining control-tag open after complete tags were removed. Do not require balanced
 * quotes — models often split mid-attribute (`label="Bo`).
 */
const INCOMPLETE_TRAILING_CONTROL_TAG_RE = new RegExp(
    `<(?:${TAG_NAME_ALT})\\b`,
    'i'
);

const ORPHAN_SELF_CLOSE_LINE_RE = /^[ \t]*\/>[ \t]*$/gm;
const ORPHAN_ATTR_RESIDUE_LINE_RE = /^[ \t]*[^<\n]+\/>[ \t]*$/gm;
const FENCED_CODE_BLOCK_RE = /```[\s\S]*?```/g;
const HTML_COMMENT_RE = /<!--[\s\S]*?--?>/g;
const INCOMPLETE_TRAILING_HTML_COMMENT_RE = /<!--[\s\S]*$/;

/**
 * @param {string} segment
 * @returns {string}
 */
function stripOrphanLines(segment) {
    ORPHAN_SELF_CLOSE_LINE_RE.lastIndex = 0;
    ORPHAN_ATTR_RESIDUE_LINE_RE.lastIndex = 0;
    HTML_COMMENT_RE.lastIndex = 0;
    return segment
        .replace(HTML_COMMENT_RE, '')
        .replace(INCOMPLETE_TRAILING_HTML_COMMENT_RE, '')
        .replace(ORPHAN_SELF_CLOSE_LINE_RE, '')
        .replace(ORPHAN_ATTR_RESIDUE_LINE_RE, '');
}

/**
 * Apply orphan-line cleanup only outside closed markdown fences.
 * @param {string} text
 * @returns {string}
 */
function stripOrphanResiduesOutsideFences(text) {
    FENCED_CODE_BLOCK_RE.lastIndex = 0;
    let out = '';
    let last = 0;
    let match;
    while ((match = FENCED_CODE_BLOCK_RE.exec(text)) !== null) {
        out += stripOrphanLines(text.slice(last, match.index));
        out += match[0];
        last = match.index + match[0].length;
    }
    out += stripOrphanLines(text.slice(last));
    return out;
}

/**
 * @param {string|null|undefined} text
 * @returns {string}
 */
export function stripChartOpsFromStreamingText(text) {
    if (text == null || typeof text !== 'string' || text.length === 0) {
        return '';
    }

    COMPLETE_CONTROL_TAG_RE.lastIndex = 0;
    let out = text.replace(COMPLETE_CONTROL_TAG_RE, '');

    const incompleteAt = out.search(INCOMPLETE_TRAILING_CONTROL_TAG_RE);
    if (incompleteAt !== -1) {
        out = out.slice(0, incompleteAt);
    }

    out = stripOrphanResiduesOutsideFences(out);

    return out
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n');
}

export default stripChartOpsFromStreamingText;
